import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { LookupDto } from '../models/common/lookup.dto';
import { CompanyShortDto } from '../models/companies/companyShortDto';
import { MedicalCheckupResult } from '../models/enums/medicalCheckupResult';
import { MedicalCheckupStatus } from '../models/enums/medicalCheckupStatus';
import { MedicalCheckupShortDto } from '../models/medicalCheckups/medicalCheckupShortDto';
import { RejectReasonDto } from '../models/medicalCheckups/rejectReasonDto';

dayjs.extend(utc);

export interface NewMedicalCheckupsListFilter {
    fio: string;
    company: { id: number } | null;
    terminalIds: number[];
}

export interface HistoryMedicalCheckupsListFilter {
    fromDate: string | null;
    toDate: string | null;
    medicResult: MedicalCheckupResult | null;
    fio: string;
    company: CompanyShortDto | null;
    terminals: LookupDto[];
    status: 'all' | MedicalCheckupStatus;
    rejectReasons: RejectReasonDto[];
}

const NEW_LIST_STATUSES = [MedicalCheckupStatus.New, MedicalCheckupStatus.SigningError];
const HISTORY_LIST_STATUSES = [MedicalCheckupStatus.Verdict, MedicalCheckupStatus.Signing];

function matchesFio(checkup: MedicalCheckupShortDto, fio: string): boolean {
    if (!fio) {
        return true;
    }
    return checkup.fio.toLowerCase().includes(fio.toLowerCase());
}

function matchesCompanyId(checkup: MedicalCheckupShortDto, companyId: number | null | undefined): boolean {
    if (!companyId) {
        return true;
    }
    return checkup.company.id === companyId;
}

function matchesTerminalIds(checkup: MedicalCheckupShortDto, terminalIds: number[]): boolean {
    if (!terminalIds.length) {
        return true;
    }
    return checkup.terminalId != null && terminalIds.includes(checkup.terminalId);
}

function matchesDateRange(checkup: MedicalCheckupShortDto, fromDate: string | null, toDate: string | null): boolean {
    const startedAt = dayjs.utc(checkup.date).add(checkup.timeShift ?? 0, 'hour');

    if (fromDate) {
        const from = dayjs.utc(fromDate).startOf('day');
        if (startedAt.isBefore(from)) {
            return false;
        }
    }

    if (toDate) {
        const toExclusive = dayjs.utc(toDate).add(1, 'day').startOf('day');
        if (!startedAt.isBefore(toExclusive)) {
            return false;
        }
    }

    return true;
}

export function matchesNewListFilter(
    checkup: MedicalCheckupShortDto,
    filter: NewMedicalCheckupsListFilter
): boolean {
    if (!NEW_LIST_STATUSES.includes(checkup.status)) {
        return false;
    }

    if (!matchesFio(checkup, filter.fio)) {
        return false;
    }

    if (!matchesCompanyId(checkup, filter.company?.id)) {
        return false;
    }

    return matchesTerminalIds(checkup, filter.terminalIds);
}

export function matchesHistoryListFilter(
    checkup: MedicalCheckupShortDto,
    filter: HistoryMedicalCheckupsListFilter
): boolean {
    const allowedStatuses =
        filter.status === 'all' ? HISTORY_LIST_STATUSES : [filter.status];

    if (!allowedStatuses.includes(checkup.status)) {
        return false;
    }

    if (!matchesDateRange(checkup, filter.fromDate, filter.toDate)) {
        return false;
    }

    if (filter.medicResult != null && checkup.medicResult !== filter.medicResult) {
        return false;
    }

    const selectedReasonIds = filter.rejectReasons.map(r => r.id);
    if (selectedReasonIds.length > 0) {
        const checkupReasonIds = checkup.rejectReasonIds ?? [];
        if (!selectedReasonIds.some(id => checkupReasonIds.includes(id))) {
            return false;
        }
    }

    if (!matchesFio(checkup, filter.fio)) {
        return false;
    }

    if (!matchesCompanyId(checkup, filter.company?.id)) {
        return false;
    }

    const terminalIds = filter.terminals.map(t => t.id);
    return matchesTerminalIds(checkup, terminalIds);
}
